import React from 'react';
import type { Onboarding, ValidationErrors } from '../../../types/wizard';
import '../ClinicCreationWizard.css';

interface OnboardingFormProps {
  values: Onboarding;
  onChange: (data: Partial<Onboarding>) => void;
  errors: ValidationErrors;
  /**
   * When false, hides the profile-owned fields (preferred appointment method,
   * reporting method, W-9) — the onboarding endpoint pins those to their
   * current values for users without UserCanEditClinicProfile, so offering
   * them to such users silently loses their input (SLV2-3155). Defaults to
   * true (the creation wizard is only reachable by profile editors).
   */
  showProfileOwnedFields?: boolean;
}

/**
 * Chain-of-custody vendors and the onboarding field holding each vendor's
 * platform code. Same single home as the profile's Capabilities section
 * (SLV2-2999): pick the vendor, then enter its code right beside it.
 */
const ECOC_VENDORS: ReadonlyArray<{ name: string; codeField: 'eScreenCode' | 'formFoxCode' | 'questCode' }> = [
  { name: 'eScreen', codeField: 'eScreenCode' },
  { name: 'FormFox', codeField: 'formFoxCode' },
  { name: 'Quest', codeField: 'questCode' },
];

/** Matches the API's [Range(0, 999)] on NumberOfCertifiedDOTProfessionals. */
export const MAX_DOT_PROFESSIONALS = 999;

const OnboardingForm: React.FC<Readonly<OnboardingFormProps>> = ({
  values,
  onChange,
  errors,
  showProfileOwnedFields = true,
}) => {
  // Helper to parse CSV chain of custody into array
  const getChainOfCustodyArray = (): string[] => {
    if (!values.electronicChainOfCustody) return [];
    return values.electronicChainOfCustody.split(',').filter(s => s.trim());
  };

  // Helper to update chain of custody as CSV. 'None' is mutually exclusive
  // with the provider options: checking None clears the providers and
  // checking a provider clears None, so an invalid 'None,eScreen' CSV can't
  // be produced (SLV2-3155 review).
  // A vendor's code box is disabled once the vendor is unticked, so the code is
  // cleared with it (and all three when None is chosen) rather than silently saved.
  const updateChainOfCustody = (provider: string, checked: boolean) => {
    const current = getChainOfCustodyArray();
    let updated: string[];
    const patch: Partial<Onboarding> = {};
    if (provider === 'None') {
      updated = checked ? ['None'] : [];
      if (checked) {
        for (const vendor of ECOC_VENDORS) patch[vendor.codeField] = '';
      }
    } else {
      updated = checked
        ? [...current.filter(p => p !== 'None'), provider]
        : current.filter(p => p !== provider);
      if (!checked) {
        const codeField = ECOC_VENDORS.find(v => v.name === provider)?.codeField;
        if (codeField) patch[codeField] = '';
      }
    }
    patch.electronicChainOfCustody = updated.join(',');
    onChange(patch);
  };

  const chainOfCustodyArray = getChainOfCustodyArray();

  return (
    <>
      {!showProfileOwnedFields && (
        <p className="form-hint">
          Appointment method, reporting method, and W-9 status are managed by profile editors
          and are not shown here.
        </p>
      )}

      {showProfileOwnedFields && (
        <div className="form-group">
          <label htmlFor="preferredMethodOfAppointment">Preferred Appointment Method</label>
          <select
            id="preferredMethodOfAppointment"
            value={values.preferredMethodOfAppointment ?? ''}
            onChange={(e) => onChange({ preferredMethodOfAppointment: e.target.value })}
            className="form-select-full"
          >
            <option value="">Select...</option>
            <option value="Call">Call</option>
            <option value="Email">Email</option>
            <option value="Walk-in">Walk-in</option>
          </select>
        </div>
      )}

      <div className="form-group">
        <label id="ecoc-group-label">Electronic Chain of Custody</label>
        <div className="form-checkbox-list" role="group" aria-labelledby="ecoc-group-label">
          <label className="form-checkbox-row">
            <input
              type="checkbox"
              id="ecoc-none"
              checked={chainOfCustodyArray.includes('None')}
              onChange={(e) => updateChainOfCustody('None', e.target.checked)}
            />
            None
          </label>
          {/* One row per vendor: the checkbox, then that vendor's platform code
              beside it, enabled only while the vendor is checked. */}
          {ECOC_VENDORS.map((vendor) => {
            const checked = chainOfCustodyArray.includes(vendor.name);
            const inputId = `ecoc-code-${vendor.codeField}`;
            return (
              <div key={vendor.name} className="form-vendor-row">
                <label className="form-checkbox-row form-vendor-row-name">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => updateChainOfCustody(vendor.name, e.target.checked)}
                  />
                  {vendor.name}
                </label>
                <input
                  type="text"
                  id={inputId}
                  className="form-vendor-row-code"
                  value={values[vendor.codeField] ?? ''}
                  onChange={(e) => onChange({ [vendor.codeField]: e.target.value })}
                  placeholder={`${vendor.name} code (optional)`}
                  aria-label={`${vendor.name} code`}
                  disabled={!checked}
                />
              </div>
            );
          })}
        </div>
      </div>

      {showProfileOwnedFields && (
        <div className="form-group">
          <label htmlFor="reportingMethod">Reporting Method</label>
          <select
            id="reportingMethod"
            value={values.reportingMethod ?? ''}
            onChange={(e) => onChange({ reportingMethod: e.target.value })}
            className="form-select-full"
          >
            <option value="">Select...</option>
            <option value="Email">Email</option>
            <option value="Fax">Fax</option>
            <option value="Portal">Portal</option>
          </select>
        </div>
      )}

      <div className="form-group">
        <label className="form-checkbox-row">
          <input
            type="checkbox"
            checked={values.dotCertification ?? false}
            onChange={(e) => onChange({
              dotCertification: e.target.checked,
              numberOfCertifiedDOTProfessionals: e.target.checked ? values.numberOfCertifiedDOTProfessionals : undefined
            })}
          />
          DOT Certification
        </label>
      </div>

      {values.dotCertification && (
        <div className="form-group">
          <label htmlFor="numberOfDOTPros">
            Number of Certified DOT Professionals <span className="required">*</span>
          </label>
          <input
            type="number"
            id="numberOfDOTPros"
            min="1"
            max={MAX_DOT_PROFESSIONALS}
            value={values.numberOfCertifiedDOTProfessionals ?? ''}
            onChange={(e) => onChange({
              numberOfCertifiedDOTProfessionals: e.target.value ? Number.parseInt(e.target.value, 10) : undefined
            })}
            className={errors.numberOfCertifiedDOTProfessionals ? 'error' : ''}
            placeholder={`1 to ${MAX_DOT_PROFESSIONALS}`}
          />
          {errors.numberOfCertifiedDOTProfessionals && (
            <div className="error-text">{errors.numberOfCertifiedDOTProfessionals}</div>
          )}
        </div>
      )}

      {/* Translation Services is a capability (Capabilities step), not onboarding data —
          Clinic Network decision 02, Morgan 2026-09-21 (SLV2-3301). */}

      {showProfileOwnedFields && (
        <div className="form-group">
          <label className="form-checkbox-row">
            <input
              type="checkbox"
              checked={values.w9OnFile ?? false}
              onChange={(e) => onChange({ w9OnFile: e.target.checked })}
            />
            W9 On File
          </label>
        </div>
      )}
    </>
  );
};

export default OnboardingForm;
